import { AiConnectionsForm } from './ai-connections.form';
import { AiCapabilityFormArray } from './ai-capability.form';
import { AiKnowledgeFormArray } from './ai-knowledge.form';
import { FormControl, FormGroup } from '@angular/forms';
import {
  AssistantInterface,
  ConnectionInterface,
  NamespaceInterface,
  AssistantType,
  GoalType,
  PromptModuleApiService,
} from '@vendasta/ai-assistants';
import { AiAssistant } from '../interfaces/ai-assistant.interface';
import { KnowledgeSource } from '@vendasta/embeddings';
import { AiVoiceConfigurationForm } from './ai-voice-model-config.form';
import { AiToolFormRegistryService } from '../services/ai-tool-form-registry.service';
import { AiModelConfigurationForm } from './ai-model-config.form';
import { DEFAULT_ASSISTANT_NAMES, VoiceProviderFeatureFlags } from '../ai-assistant.constants';

// Assistant
export type AiAssistantFormControls = {
  id: FormControl<string | null>;
  namespace: FormControl<NamespaceInterface | null>;
  name: FormControl<string | null>;
  avatarUrl: FormControl<string | null>;
  capabilities: AiCapabilityFormArray;
  connections: AiConnectionsForm;
  knowledge: AiKnowledgeFormArray;
  voiceConfig: AiVoiceConfigurationForm;
  type: FormControl<AssistantType | null>;
  model: AiModelConfigurationForm;
};

export class AiAssistantForm extends FormGroup<AiAssistantFormControls> {
  constructor(
    promptModuleApiService: PromptModuleApiService,
    assistant?: AiAssistant,
    promptModuleContents?: Record<string, string>,
    knowledgeSources?: KnowledgeSource[],
    toolFormRegistry?: AiToolFormRegistryService,
    voiceFeatures?: VoiceProviderFeatureFlags,
  ) {
    const type = assistant?.assistant.type ?? AssistantType.ASSISTANT_TYPE_CUSTOM;
    let goals = assistant?.assistant.configurableGoals?.filter((goal) => goal !== undefined) || [];
    const model = assistant?.assistant?.config?.models?.[0];

    // Ensure there's always at least one personality goal per assistant.
    if (!goals.some((goal) => goal.goal?.type === GoalType.GOAL_TYPE_PERSONALITY)) {
      goals = [
        {
          goal: {
            type: GoalType.GOAL_TYPE_PERSONALITY,
            name: `${assistant?.assistant.name || 'Assistant'} Personality`,
            namespace: assistant?.assistant.namespace,
            promptModules: [
              {
                namespace: assistant?.assistant.namespace,
              },
            ],
          },
        },
        ...goals,
      ];
    }

    super({
      id: new FormControl({ value: assistant?.assistant.id ?? null, disabled: true }),
      type: new FormControl({ value: type, disabled: true }),
      namespace: new FormControl(assistant?.assistant.namespace ?? null),
      name: new FormControl(assistant?.assistant.name || ''),
      avatarUrl: new FormControl(assistant?.assistant.avatarUrl || ''),
      capabilities: new AiCapabilityFormArray(
        promptModuleApiService,
        goals,
        promptModuleContents || {},
        toolFormRegistry,
      ),
      connections: new AiConnectionsForm(assistant?.connections || [], assistant?.assistant.id || ''),
      knowledge: new AiKnowledgeFormArray(knowledgeSources ?? []),
      voiceConfig: new AiVoiceConfigurationForm(assistant?.assistant?.config?.voiceConfig, voiceFeatures),
      model: new AiModelConfigurationForm(model),
    });
  }

  toAssistant(localNamespace: NamespaceInterface): AssistantInterface {
    const isVoiceAssistant = this.controls.type.getRawValue() === AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST;
    const model = this.controls.model.toAssistantModel();

    const defaultName = DEFAULT_ASSISTANT_NAMES.get(
      this.controls.type.getRawValue() ?? AssistantType.ASSISTANT_TYPE_CUSTOM,
    );
    return {
      id: this.controls.id.getRawValue() ?? undefined,
      type: this.controls.type.getRawValue() ?? AssistantType.ASSISTANT_TYPE_CUSTOM,
      namespace: this.controls.namespace.getRawValue() ?? localNamespace,
      name: this.controls.name.getRawValue() || defaultName,
      avatarUrl: this.controls.avatarUrl.getRawValue() ?? undefined,
      configurableGoals: this.controls.capabilities.toConfigurableGoals(localNamespace),
      config: isVoiceAssistant
        ? {
            voiceConfig: this.controls.voiceConfig.toAssistantVoiceConfig(),
          }
        : {
            models: model ? [model] : [],
          },
    };
  }

  toConnections(): ConnectionInterface[] {
    return this.controls.connections.toConnections();
  }
}
